/* ============================================================================
   IRRELEVNT — comportamiento de la página
   ----------------------------------------------------------------------------
   Sin módulos ni bundler: los <script> se cargan en orden y todo es ámbito
   global. products.js tiene que cargar antes que este archivo.
   ========================================================================== */

const $  = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Escapa texto que va a interpolarse en HTML. Hoy el contenido lo escribimos
   nosotros en products.js, pero el día que venga de un CMS esto ya está. */
function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

/* --- Tira de fotos ---------------------------------------------------------
   Avanza sola y además SE PUEDE ARRASTRAR con el mouse o el dedo, con inercia
   al soltar. Por eso el riel lo mueve JS y no una animación de CSS: una
   animación no se puede agarrar a mitad de camino.

   Cómo no se ve el corte: la lista se repite N veces y la posición se toma
   siempre módulo `pitch` (el ancho de una copia más su hueco). Como todas las
   copias son idénticas, el salto de `pitch` a 0 es invisible, y funciona
   igual en los dos sentidos — que es lo que hace falta al arrastrar hacia
   atrás. La forma de cada tarjeta sale de `ratio`/`pos` en products.js.        */
const MARQUEE_SPEED = 34; // px por segundo del avance automático

/* Cuánto sube o baja cada tarjeta respecto de la fila. Es una tabla fija y no
   Math.random() por dos motivos: con azar cada recarga movería la tira, y —
   peor — las copias no coincidirían entre sí y el ciclo mostraría un salto.
   Los valores alternan de signo para que no se lea como una escalera.
   Bajado de ±18 a ±11 el 2026-08-11: el usuario lo vio demasiado marcado. */
const STAGGER = [0, -10, 6, -4, 9, -8, 3, -11, 7, -2];

const FLOAT_AMP = 4;      // px del vaivén, arriba y abajo

/* Las tarjetas crecen a medida que se van de la pantalla. Dos decisiones:

   1. La escala depende de DÓNDE está la tarjeta, no de hacia dónde va. Si
      dependiera del sentido de la marcha, al arrastrar al revés se invertiría
      de golpe. Al ser simétrica también entran grandes por el otro lado, que
      es lo que hace que se lea continua.

   2. Cuánto puede crecer NO es una preferencia, lo fija la separación entre
      tarjetas: lo que sobresale del hueco propio se le come el aire a la
      vecina. Medido en el navegador: con 1 → 1,06 dos vecinas del tercio
      exterior se pisaban ~4px. Con 8,9px de separación y tarjetas de ~256px,
      el techo está en 1,04.
      También se probó achicar el centro (0,975 → 1,035) para ganar recorrido:
      no sirve. Al achicar las del medio, los huecos del CENTRO crecen — daban
      15px, más que los 14 que el usuario ya había visto como demasiado
      marcados. Justo lo contrario de lo pedido.
      Si se sube `EDGE_SCALE`, hay que volver a medir el solape barriendo un
      ciclo entero de posiciones. Mirarlo no alcanza: pasa en el borde, donde
      las tarjetas ya están cortadas por la pantalla. */
const CENTER_SCALE = 1;
const EDGE_SCALE = 1.037;

function setupMarquee() {
  const wrap = $("#marquee");
  const track = $("#marqueeTrack");
  if (!wrap || !track || typeof MARQUEE === "undefined") return;

  /* Sin `loading="lazy"` a propósito: la tira se arrastra, así que una foto
     puede entrar en pantalla de golpe y no da tiempo a cargarla — se verían
     cajas vacías al arrastrar rápido. Son 10 archivos, y 6 de ellos ya los
     usa la colección, así que la descarga extra real es mínima; además las
     copias repiten los mismos archivos y el navegador no los vuelve a pedir.

     El desnivel y el vaivén NO van en el marcado: los calcula el bucle a
     partir del índice DENTRO de la lista, así todas las copias quedan
     idénticas y la costura del ciclo sigue siendo invisible. Si salieran de
     Math.random() o del índice global, el salto se vería. */
  const setHTML = MARQUEE.map((m) => `
    <div class="m-card" style="--ar:${m.ratio}${m.pos ? `;--pos:${m.pos}` : ""}">
      <img src="${esc(m.src)}" alt="" width="${m.w}" height="${m.h}"
           draggable="false" decoding="async">
    </div>`).join("");

  let pitch = 0;      // ancho de una copia + un hueco
  let cards = [];     // {el, mid, dy, phase, freq, last} por tarjeta
  let offset = 0;     // px recorridos; siempre dentro de [0, pitch)
  let velocity = 0;   // px/s, la inercia de después de soltar
  let dragging = false;
  let hovering = false;
  let visible = true;
  let raf = null;
  let prevT = 0;
  let lastX = 0, lastT = 0, prevX = 0, prevSampleT = 0;

  /* Se pinta una copia, se mide, y recién ahí se sabe cuántas hacen falta
     para cubrir la ventana en cualquier posición del ciclo. Con dos copias
     fijas quedaba un hueco vacío a la derecha en pantallas muy anchas. */
  function build() {
    track.innerHTML = setHTML;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    pitch = track.getBoundingClientRect().width + gap;
    if (!pitch) return;
    const copies = Math.max(2, Math.ceil((wrap.clientWidth + pitch) / pitch) + 1);
    track.innerHTML = setHTML.repeat(copies);

    /* Se mide una sola vez por construcción, con `offsetLeft`/`offsetWidth`:
       son medidas de maquetación y NO las altera el `transform` que se les
       escribe después. Con getBoundingClientRect() la medida ya vendría
       escalada y el cálculo se realimentaría a sí mismo. */
    const N = MARQUEE.length;
    const base = track.offsetLeft;
    cards = Array.from(track.children, (el, k) => {
      const i = k % N;
      return {
        el,
        mid: el.offsetLeft - base + el.offsetWidth / 2,
        dy: STAGGER[i % STAGGER.length],
        phase: i * 0.83,
        freq: (2 * Math.PI) / (6 + i * 0.37), // ciclos de 6 a 9,3 s
        last: "",
      };
    });
  }

  /* Escribe la posición del riel y el `transform` de cada tarjeta. Sale todo
     de acá y no de CSS porque la escala depende de dónde cae la tarjeta en la
     pantalla, y una animación de CSS pisaría el `transform` en línea. */
  function apply(now) {
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;

    const t = (now === undefined ? performance.now() : now) / 1000;
    const half = wrap.clientWidth / 2 || 1;
    const flota = !prefersReducedMotion();

    for (const c of cards) {
      // 0 en el centro de la pantalla, 1 en los bordes
      let d = Math.abs(c.mid - offset - half) / half;
      if (d > 1) d = 1;
      // Al cuadrado: casi no cambia en el centro y crece sobre el final.
      const s = CENTER_SCALE + (EDGE_SCALE - CENTER_SCALE) * d * d;
      const y = c.dy + (flota ? Math.sin(t * c.freq + c.phase) * FLOAT_AMP : 0);

      // Escribir solo si cambió: evita ensuciar el estilo en cada fotograma.
      const v = `translateY(${y.toFixed(2)}px) scale(${s.toFixed(4)})`;
      if (v !== c.last) { c.el.style.transform = v; c.last = v; }
    }
  }

  function wrapOffset() {
    if (pitch > 0) offset = ((offset % pitch) + pitch) % pitch;
  }

  function tick(now) {
    const dt = Math.min((now - prevT) / 1000, 0.05); // un salto de pestaña no debe pegar un tirón
    prevT = now;

    if (!dragging) {
      if (Math.abs(velocity) > 2) {
        offset -= velocity * dt;
        velocity *= Math.pow(0.93, dt * 60); // fricción independiente de los fps
      } else {
        velocity = 0;
        if (!hovering && !prefersReducedMotion()) offset += MARQUEE_SPEED * dt;
      }
      wrapOffset();
    }
    // Fuera del `if`: aunque el riel esté quieto (arrastrando sin mover la
    // mano, o con el mouse encima), el vaivén de las tarjetas sigue.
    apply(now);
    raf = requestAnimationFrame(tick);
  }

  function start() {
    if (raf !== null) return;
    prevT = performance.now();
    raf = requestAnimationFrame(tick);
  }
  function stop() {
    if (raf === null) return;
    cancelAnimationFrame(raf);
    raf = null;
  }

  /* --- Arrastre --- */
  wrap.addEventListener("pointerdown", (e) => {
    if (e.button !== undefined && e.button !== 0) return; // solo botón principal
    dragging = true;
    velocity = 0;
    lastX = prevX = e.clientX;
    lastT = prevSampleT = performance.now();
    wrap.classList.add("is-dragging");
    /* La captura es una mejora, no un requisito: sirve para que el arrastre
       siga funcionando si el puntero se va fuera de la tira. Si el navegador
       la rechaza (puntero ya soltado, evento sintético), el arrastre tiene
       que seguir andando igual en vez de cortarse con una excepción. */
    try { wrap.setPointerCapture(e.pointerId); } catch (_) { /* sin captura */ }
  });

  wrap.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const now = performance.now();
    offset -= e.clientX - lastX; // el contenido acompaña al puntero
    wrapOffset();
    apply();
    /* Se guarda la muestra ANTERIOR además de la última: si el puntero se
       queda quieto justo antes de soltar, la velocidad tiene que dar 0 y no
       la de hace medio segundo. */
    prevX = lastX; prevSampleT = lastT;
    lastX = e.clientX; lastT = now;
  });

  function endDrag(e) {
    if (!dragging) return;
    dragging = false;
    wrap.classList.remove("is-dragging");
    try {
      if (e.pointerId !== undefined && wrap.hasPointerCapture(e.pointerId)) {
        wrap.releasePointerCapture(e.pointerId);
      }
    } catch (_) { /* no había captura que soltar */ }
    const dt = (lastT - prevSampleT) / 1000;
    // Si la última muestra ya tiene tiempo, el dedo estaba parado: sin inercia.
    const quieto = performance.now() - lastT > 90;
    velocity = (dt > 0 && !quieto) ? Math.max(-2600, Math.min(2600, (lastX - prevX) / dt)) : 0;

    /* En táctil el dedo también dispara `pointerenter`, y el `pointerleave`
       que lo cancela no siempre llega. Sin esto, la tira se quedaba frenada
       para siempre después del primer arrastre en el teléfono. */
    if (e.pointerType !== "mouse") hovering = false;
  }

  wrap.addEventListener("pointerup", endDrag);
  wrap.addEventListener("pointercancel", endDrag);

  /* Al pasar el mouse por encima se frena, para poder agarrar la foto que uno
     quiere en vez de perseguirla. Solo con mouse: en táctil no existe "estar
     encima sin tocar", y frenar ahí dejaría la tira parada de más. */
  wrap.addEventListener("pointerenter", (e) => { hovering = e.pointerType === "mouse"; });
  wrap.addEventListener("pointerleave", () => { hovering = false; });

  /* Nada de trabajo por fotograma cuando la tira no está en pantalla ni
     cuando la pestaña está en segundo plano. */
  if ("IntersectionObserver" in window) {
    new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible && !document.hidden) start(); else stop();
    }, { threshold: 0 }).observe(wrap);
  }
  document.addEventListener("visibilitychange", () => {
    if (document.hidden || !visible) stop(); else start();
  });

  let resizeT = null;
  window.addEventListener("resize", () => {
    clearTimeout(resizeT);
    resizeT = setTimeout(() => { build(); wrapOffset(); apply(); }, 150);
  });

  build();
  apply();
  start();
}

/* --- Colección ------------------------------------------------------------
   Solo foto, nombre y acabado: las tarjetas son editoriales, como en el video
   de referencia. La configuración de acabado y medidas ya no vive acá — está
   en una sola sección al final (ver setupCotizador()). Una ficha por tarjeta
   volvía la grilla una pared de formularios y las fotos dejaban de ser el
   asunto.                                                                    */
const EJES = ["alto", "ancho", "largo"];

/* La medida de las piezas que no se ajustan, para mostrarla como una línea. */
function medidaFija(p) {
  return EJES.map((e) => p.medidas[e].def).join(" × ") + " cm";
}

/* Qué significa el número que acompaña. Son tres casos y no dan lo mismo:

     · a medida       → el número es el punto de partida; se fabrica al tamaño
                        que pidas (volumen cerrado).
     · por módulo     → el número es UNA unidad, no la pieza entera; la pieza
                        se arma juntando las que quieras (`modular` en
                        products.js).
     · medida estándar→ esa medida es la única que hay.

   Vive acá y no repetido en cada plantilla porque lo usan la tarjeta de la
   colección y el cotizador, y tienen que decir lo mismo en los dos lados. */
function notaMedida(p) {
  if (p.modular) return "por módulo";
  return p.ajustable ? "a medida" : "medida estándar";
}

/* La línea de medida entera, tal como se lee en pantalla. La escriben CUATRO
   sitios —la tarjeta de colección, la ventana de acabado, la línea del pedido
   y el cotizador— y tienen que decir exactamente lo mismo; antes cada uno
   componía `medidaFija() · notaMedida()` por su cuenta.

   La pieza modular es la razón de que esto exista (2026-08-14). Cuando su
   importe pasó a ser el de la configuración de la foto —seis módulos, ₡180 000—
   y no el de una unidad, la fórmula vieja quedó mintiendo: "30 × 30 × 40 cm ·
   por módulo" al lado de ₡180 000 se lee como que ESA unidad cuesta eso. Con
   "6 módulos de 30 × 30 × 40 cm" el importe y la medida hablan de lo mismo.

   `notaMedida()` sigue existiendo y sigue devolviendo "por módulo": es el
   sufijo, no la frase, y el cotizador lo puede necesitar suelto.              */
function medidaLinea(p, n) {
  if (p.modular && p.modulos) {
    const cant = n || p.modulos;
    return `${cant} ${cant === 1 ? "módulo" : "módulos"} de ${medidaFija(p)}`;
  }
  return `${medidaFija(p)} · ${notaMedida(p)}`;
}

/* Cuánto cuestan `n` módulos. La regla la dictó el usuario el 2026-08-14: el
   conjunto de seis lleva descuento (₡180 000, o sea 30 000 el módulo) y el
   suelto es más caro (₡35 000).

   **Cada grupo completo de seis va a precio de conjunto y el resto va suelto.**
   Se generaliza así, en vez de aplicar el descuento solo a los primeros seis,
   por dos razones: el descuento es del conjunto, y quien pide doce está
   pidiendo dos conjuntos — cobrarle 180 000 + 6 × 35 000 (₡390 000) en lugar
   de 2 × 180 000 (₡360 000) sería cobrarle de más por comprar más. Con esta
   regla el precio nunca supera al de sumar sueltos y sigue subiendo con la
   cantidad (5 → ₡175 000, 6 → ₡180 000, 11 → ₡355 000, 12 → ₡360 000).

   Vive acá y no en la ventana porque la usan tres sitios: el importe que se
   ve al elegir, la línea del pedido y el total.                               */
function precioModular(p, n) {
  const conjuntos = Math.floor(n / p.modulos);
  const sueltos = n % p.modulos;
  return conjuntos * p.precio + sueltos * p.precioModulo;
}

/* El precio de UNA unidad de una línea del pedido (sin multiplicar por la
   cantidad de veces que se agregó). En la modular depende de cuántos módulos
   se pidieron, así que no alcanza con `p.precio`. */
function precioLinea(l, p) {
  if (!p || !p.precio) return 0;
  if (p.modular) return precioModular(p, l.modulos || p.modulos);
  return p.precio;
}

/* --- Qué se cobra y qué se cotiza (preparación para Tilopay, 2026-08-14) ----
   Decisión del negocio: **lo estándar se cobra en línea, lo personalizado va
   por cotización.** Y lo que decide NO son las medidas sino POR DÓNDE entró
   la línea (elección del usuario): todo lo que sale del cotizador se cotiza,
   aunque no se haya cambiado un solo número, porque el cotizador es el camino
   de "a tu medida". Lo que entra por la tarjeta o por un accesorio es medida
   estándar y se cobra.

   Por eso cada línea guarda `via`, y no se deduce de las medidas: deducirlo
   funcionaría hoy y se rompería el día que una pieza estándar comparta medidas
   con una pedida a gusto.                                                    */
const VIA_ESTANDAR = "estandar";
const VIA_COTIZADOR = "cotizador";

/* Líneas guardadas ANTES de que existiera `via` (carritos a medio usar de
   alguien que ya había entrado al sitio). No tienen origen, así que se deduce
   por única vez comparando con las medidas de fábrica — que es exactamente la
   regla que NO se usa para las líneas nuevas, pero acá es lo único que hay. Se
   inclina a "cotizador" ante la duda: mandar a cotizar algo que se podía
   cobrar es un trámite de más; cobrar algo que iba a cotización es cobrarle
   mal a un cliente.                                                          */
function viaDeLinea(l, p) {
  if (l.via) return l.via;
  if (!p || l.tipo === "accesorio") return VIA_ESTANDAR;
  const igualALaEstandar = EJES.every((e) => Number(l[e]) === p.medidas[e].def);
  return igualALaEstandar ? VIA_ESTANDAR : VIA_COTIZADOR;
}

/* Una línea se cobra si entró como estándar Y tiene precio. Las dos
   condiciones importan: una pieza sin precio cargado tampoco se puede cobrar,
   por muy estándar que sea. */
function seCobra(l) {
  const p = buscarProducto(l);
  return !!(p && p.precio && viaDeLinea(l, p) === VIA_ESTANDAR);
}

/* Reparte el pedido en los dos caminos. Un solo sitio que lo decida: lo usan
   el panel, los dos totales y los dos botones. */
function repartirPedido() {
  return {
    cobrables: pedido.filter(seCobra),
    aCotizar: pedido.filter((l) => !seCobra(l)),
  };
}

const sumaDe = (lineas) =>
  lineas.reduce((s, l) => s + precioLinea(l, buscarProducto(l)) * l.cant, 0);

/* El importe que se MUESTRA de una línea, en el panel y en el mensaje.

   ⚠ No es lo mismo que `precioLinea()`, y la diferencia importa: una pieza
   pedida por el cotizador a una medida distinta de la estándar **no cuesta el
   precio de lista**. Mostrarle ₡320 000 a alguien que pidió esa mesa 40 cm más
   larga sería darle un precio que después no se le va a respetar. En esas
   líneas el importe es "A cotizar" hasta que el taller confirme.              */
function importeLinea(l) {
  if (!seCobra(l)) return "A cotizar";
  return colones(precioLinea(l, buscarProducto(l)) * l.cant);
}

/* Colones. Un solo sitio para el formato: si mañana hay más piezas con precio
   no puede haber dos maneras de escribirlo dando vueltas.

   Separador de MILES con coma, que es como lo escribe el usuario y como se ve
   en el comercio de Costa Rica. Ojo: el estándar de la configuración regional
   `es-CR` no es ese — `Intl.NumberFormat('es-CR')` devuelve "₡180 000", con
   espacio. Se eligió la coma a conciencia, no por desconocerlo; para volver al
   estándar, esta función es lo único que hay que cambiar.

   Sin decimales: no se cobran céntimos de colón.                             */
function colones(n) {
  return "₡" + Math.round(n).toLocaleString("en-US");
}

function renderPiezas() {
  const grid = $("#piezaGrid");
  if (!grid || typeof PIEZAS === "undefined") return;

  grid.innerHTML = PIEZAS.map((p, i) => `
    <figure class="pieza reveal" style="--d:${(i % 3) * 80}ms" data-id="${p.id}">
      <div class="pieza-media">
        <img src="${esc(p.image)}" alt="${esc(p.nombre)}"
             width="${p.w}" height="${p.h}" decoding="async" loading="lazy">
      </div>

      <!-- La etiqueta de la derecha muestra los dos acabados: es la elección
           que se ofrece en todas las piezas, no una ficha de lo que se ve en
           la foto.

           La segunda línea es la medida. El sufijo NO es decorativo y no se
           puede unificar: "medida estándar" significa que esa medida es la
           única que hay (varilla y vidrio, ver el campo ajustable en
           products.js), mientras que "a medida" avisa de que el número es solo
           el punto de partida y se fabrica al tamaño que pidas. Poner el
           número pelado haría pasar por fija una medida que no lo es. Es el
           mismo par de términos que usa el cotizador, a propósito.

           OJO: este comentario vive DENTRO de un template literal. Nada de
           comillas invertidas acá adentro — cierran la plantilla y parten el
           archivo (pasó al escribirlo). -->
      <figcaption class="pieza-cap">
        <span class="pieza-cap-fila">
          <span class="pieza-nombre">${p.handle
            ? `<a href="/products/${esc(p.handle)}">${esc(p.nombre)}</a>`
            : esc(p.nombre)}</span>
          ${p.precio
            ? `<span class="pieza-precio">${colones(p.precio)}</span>`
            : `<span class="micro">Espejo / Pulido</span>`}
        </span>
        <span class="pieza-med">
          ${medidaLinea(p)}
        </span>
        ${bloqueCompra(p)}
      </figcaption>
    </figure>`).join("");

  /* LA TARJETA ENTERA ABRE LA VENTANA, no solo el botón (2026-08-14, pedido
     del usuario: el botón solo era "poco intuitivo"). En una grilla de fotos
     grandes, lo que la gente toca es la foto; obligar a apuntarle a un renglón
     de 10px al pie es pedirle puntería para la acción principal de la página.

     El botón NO se fue: sigue siendo la señal visible de que la tarjeta hace
     algo, y es el ÚNICO elemento enfocable de la tarjeta. Ese es el punto de
     la implementación —la tarjeta no lleva `tabindex` ni `role="button"`—:
     con los dos enfocables habría dos paradas de tabulación para una sola
     acción, y quien navega con teclado tendría que pasar dos veces por cada
     pieza. Con mouse se toca donde sea, con teclado se tabula al botón.
     Por eso también `abrir()` recibe el BOTÓN como origen y no la tarjeta: al
     cerrar, el foco tiene que volver a algo que pueda recibirlo.

     La ventana sigue siendo la que pide el acabado; esto solo cambia dónde se
     puede tocar para llegar a ella. Ver setupAcabado() más abajo. */
  grid.addEventListener("click", (e) => {
    /* Si lo que hubo fue una selección de texto (alguien arrastrando sobre el
       nombre o el precio), el clic no es un clic: no abrir nada. */
    if (String(window.getSelection())) return;

    const card = e.target.closest(".pieza");
    if (!card) return;
    const p = PIEZAS.find((x) => x.id === card.dataset.id);
    if (!p) return;

    /* El botón es el atajo y NO navega: abre la ventana de acabado. */
    if (e.target.closest(".pieza-add")) {
      if (ventanaAcabado) ventanaAcabado.abrir(p, card.querySelector(".pieza-add"));
      return;
    }

    /* El resto de la tarjeta lleva a la ficha. Sin handle (prototipo suelto,
       sin Shopify detrás) se comporta como antes. */
    if (p.handle) {
      window.location.href = "/products/" + encodeURIComponent(p.handle);
      return;
    }
    if (ventanaAcabado) ventanaAcabado.abrir(p, card.querySelector(".pieza-add"));
  });
}

/* El bloque de compra de la tarjeta. Aparece en las piezas con `precio`, que
   desde el 2026-08-14 son las NUEVE. La guarda se deja igual: si a una pieza
   se le quita el precio, su tarjeta vuelve sola a ser foto, nombre, acabados y
   medida, sin tocar esta función.

   Rehecho el 2026-08-12: la primera versión metía en la tarjeta un precio
   grande, dos chips con caja y una PÍLDORA NEGRA a todo el ancho. El usuario
   lo llamó "muy rústico" y tenía razón — además contradecía una regla que este
   mismo proyecto ya había escrito para los accesorios: "botón de texto, no
   píldora; la píldora se reserva para la acción principal", justamente para
   que la grilla no se convierta en una botonera. Lo que se hizo:

     · El PRECIO se mudó al renglón del nombre, ocupando el hueco de la
       derecha. Ese hueco tenía la etiqueta fija "Espejo / Pulido", que con un
       selector de acabado debajo era decir dos veces lo mismo. Se gana una
       línea entera y el importe cae donde el ojo ya estaba mirando.
     · El BOTÓN es de texto y comparte reglas con `.acc-add` (mismo tamaño,
       misma línea fina arriba, mismo "+"). No es un estilo parecido: es el
       mismo bloque de CSS, para que no puedan separarse con el tiempo.
     · Se cayó la nota de dos líneas que explicaba lo de la medida estándar.
       Lo decía el propio botón, que hasta el 2026-08-14 se llamó "Agregar en
       esta medida"; ahora dice **"Agregar al carrito"** (pedido del usuario).
       El dato no se perdió al cambiarlo: la medida con su sufijo está en la
       línea de arriba (`.pieza-med`) y la ventana que abre el botón lo repite
       ("Es la medida estándar. Si la necesitas en otra, cotiza a tu medida").
       El enlace al cotizador tampoco: el CTA "Cotiza a tu medida" está justo
       debajo de la grilla.

   ⚠ EL SELECTOR DE ACABADO YA NO VIVE ACÁ (2026-08-14). Estaba en la tarjeta
   como dos palabras con una barra, y con "Espejo" marcado de entrada: quien
   tocara el botón sin mirar se llevaba un acabado que nunca eligió. Ahora la
   elección la pide una ventana antes de agregar (ver setupAcabado()), sin
   ninguna opción preseleccionada. Con nueve piezas en la grilla la diferencia
   además se nota: eran nueve grupos de radios compitiendo con las fotos.

   Las reglas `.pieza-acabado` / `.acabado-op` de styles.css SIGUEN EN USO — se
   las quedó el cotizador, que las usaba desde antes. No borrarlas creyendo que
   quedaron huérfanas al vaciar esta función.                                  */
function bloqueCompra(p) {
  if (!p.precio) return "";
  return `
        <button type="button" class="pieza-add" data-id="${esc(p.id)}">Agregar al carrito</button>`;
}

/* --- Ventana de acabado ----------------------------------------------------
   Lo que se pide desde la grilla es la MEDIDA ESTÁNDAR, así que lo único que
   falta decidir es el acabado; la medida a gusto sigue siendo el cotizador, y
   la ventana lo enlaza para no dejar sin salida a quien llegó buscando eso.

   Por qué una ventana y no el selector que había en la tarjeta: con una opción
   marcada de entrada nunca se elige nada, y sin ninguna marcada la tarjeta se
   queda con un control a medio contestar a la vista de todos. En una ventana
   la pregunta tiene principio y fin.

   Decisiones que conviene no deshacer:

     · NINGUNA opción viene marcada. Es el punto del cambio. Si no se elige,
       "Agregar" no agrega: muestra el aviso y manda el foco al primer radio,
       el mismo trato que da el cotizador a una medida fuera de rango (y con
       la misma clase `.med-error`, que no usa color — la paleta no tiene
       ninguno de alerta).
     · El marcado lo arma JS y NO está en ningún .html, igual que el panel del
       pedido y por lo mismo: son dos páginas sueltas sin plantillas, y un
       bloque copiado en las dos se desincroniza solo. Sin JS no hay grilla ni
       botón, así que no se pierde nada.
     · Se arma UNA sola vez y se rellena al abrir. Nueve ventanas escondidas en
       el DOM, una por pieza, serían nueve copias del mismo diálogo.
     · Los radios se desmarcan en cada apertura (`checked = false`): si no, la
       segunda pieza abriría con lo elegido para la primera y volveríamos justo
       a la preselección que este cambio vino a sacar.
     · Al cerrar, el foco vuelve al botón de la tarjeta que la abrió — no al
       principio de la página, que dejaría perdido a quien navega con teclado.
     · La píldora acá SÍ corresponde: la regla del proyecto es que se reserva
       para la acción principal, y en esta ventana no hay otra.                */
let ventanaAcabado = null;

function setupAcabado() {
  // Solo en index.html: es la ventana de la grilla de colección.
  if (!$("#piezaGrid") || $("#acabado")) return;

  document.body.insertAdjacentHTML("beforeend", `
    <div class="elegir-scrim" id="acabadoScrim" hidden></div>
    <div class="elegir" id="acabado" hidden role="dialog" aria-modal="true"
         aria-labelledby="acabadoTitulo">
      <div class="elegir-head">
        <h2 class="micro" id="acabadoTitulo">Elige el acabado</h2>
        <button type="button" class="pedido-close" id="acabadoClose" aria-label="Cerrar">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
               fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M6 6 L18 18 M18 6 L6 18" />
          </svg>
        </button>
      </div>

      <form class="elegir-form" id="acabadoForm" novalidate>
        <div class="elegir-pieza">
          <img id="acabadoFoto" alt="" width="86" height="86">
          <!-- La región viva va acá y no en cada línea suelta: con el selector
               de módulos, la medida y el importe cambian JUNTOS, y anunciarlos
               como un solo bloque ("8 módulos de 30 × 30 × 40 cm, ₡250 000")
               es lo que hace falta oír. Dos regiones vivas se pisarían.
               OJO: este comentario vive dentro de un template literal — nada
               de comillas invertidas acá adentro, cierran la plantilla y
               parten el archivo (pasó al escribirlo). -->
          <div class="elegir-datos" aria-live="polite">
            <p class="elegir-nombre" id="acabadoNombre"></p>
            <p class="elegir-med" id="acabadoMed"></p>
            <p class="elegir-precio" id="acabadoPrecio"></p>
            <!-- Segundo precio (hoy solo el módulo suelto del Sistema Riel).
                 Arranca oculto: lo enciende abrir() en las piezas que lo
                 tengan. -->
            <p class="elegir-extra" id="acabadoExtra" hidden></p>
          </div>
        </div>

        <!-- Cantidad de módulos. Solo aparece en la pieza modular; el resto
             del catálogo se pide de a una unidad y este bloque queda oculto.
             Es el mismo dibujo que el control de cantidad del panel del
             pedido, a propósito: son la misma acción. -->
        <div class="elegir-cant" id="acabadoCant" hidden>
          <span id="acabadoCantLabel">Módulos</span>
          <div class="elegir-cant-ctrl" role="group" aria-labelledby="acabadoCantLabel">
            <button type="button" id="acabadoMenos" aria-label="Quitar un módulo">−</button>
            <span id="acabadoModulos">6</span>
            <button type="button" id="acabadoMas" aria-label="Agregar un módulo">+</button>
          </div>
        </div>

        <fieldset class="elegir-ops">
          <legend class="sr-only">Acabado</legend>
          ${ACABADOS.map((a) => `
            <label class="elegir-op">
              <input type="radio" name="acabado-elegir" value="${esc(a)}">
              <span class="elegir-op-txt">${esc(a)}</span>
              <span class="elegir-op-marca" aria-hidden="true"></span>
            </label>`).join("")}
        </fieldset>

        <p class="med-error" id="acabadoError" role="alert" hidden>
          Elige espejo o pulido para continuar.
        </p>

        <button type="submit" class="pill pill-block">Agregar al pedido</button>

        <!-- El texto lo escribe abrir(): no dice lo mismo en una pieza que se
             mide en centímetros que en la modular, que se cuenta en módulos.
             El enlace al cotizador sí es siempre el mismo. -->
        <p class="elegir-nota">
          <span id="acabadoNota"></span>
          <a class="link-line" href="cotiza.html">cotiza a tu medida</a>.
        </p>
        <!-- Decía "Desde la web todavía no se cobra: confirmamos tu pedido por
             escrito". El usuario quitó esa primera mitad el 2026-08-15 (sí se
             va a cobrar cuando el sitio quede listo) y además chocaba con lo
             que dice el propio panel del pedido, que ya ofrece pagar lo
             estándar ahí mismo. Lo que queda es lo que sigue siendo cierto en
             los dos escenarios. El aviso de que Tilopay todavía no está
             conectado vive donde corresponde: al lado del botón de pagar. -->
        <p class="form-note">
          Confirmamos tu pedido por escrito.
        </p>
      </form>
    </div>`);

  const win    = $("#acabado");
  const scrim  = $("#acabadoScrim");
  const cerrar = $("#acabadoClose");
  const form   = $("#acabadoForm");
  const err    = $("#acabadoError");
  const radios = [...form.querySelectorAll('input[name="acabado-elegir"]')];

  let pieza = null;
  let origen = null;
  let modulos = 0; // solo tiene sentido en la pieza modular

  /* Repinta medida, importe y el número del selector. Se llama al abrir y en
     cada toque de − / +, para que las tres cosas no puedan desincronizarse. */
  const pintarDatos = () => {
    const p = pieza;
    if (!p) return;

    $("#acabadoMed").textContent = medidaLinea(p, modulos);
    $("#acabadoPrecio").textContent = colones(
      p.modular ? precioModular(p, modulos) : p.precio
    );

    if (!p.modular) return;
    $("#acabadoModulos").textContent = String(modulos);
    /* Los topes se marcan deshabilitando el botón que ya no hace nada: un
       control que se puede tocar y no responde es peor que uno apagado. */
    $("#acabadoMenos").disabled = modulos <= 1;
    $("#acabadoMas").disabled = modulos >= (p.maxModulos || Infinity);
  };

  const cambiarModulos = (delta) => {
    if (!pieza || !pieza.modular) return;
    const tope = pieza.maxModulos || Infinity;
    modulos = Math.min(tope, Math.max(1, modulos + delta));
    pintarDatos();
  };

  const setOpen = (open) => {
    win.hidden = !open;
    scrim.hidden = !open;
    document.body.classList.toggle("sin-scroll", open);

    if (open) {
      /* Igual que el panel del pedido: la clase va después de forzar un
         reflow, para que la transición arranque desde el estado cerrado. El
         estado real vive en `pieza`, no en la clase — si dependiera de un
         frame que no llega, la ventana quedaría abierta sin poder cerrarse. */
      void win.offsetWidth;
      win.classList.add("is-open");
      scrim.classList.add("is-open");
      radios[0].focus();
    } else {
      win.classList.remove("is-open");
      scrim.classList.remove("is-open");
      const destino = origen && origen.isConnected ? origen : null;
      if (destino) destino.focus();
      pieza = null;
      origen = null;
    }
  };

  const abrir = (p, boton) => {
    pieza = p;
    origen = boton;

    $("#acabadoFoto").src = p.image;
    $("#acabadoNombre").textContent = p.nombre;

    /* Arranca SIEMPRE en la configuración de la foto, no en lo que se eligió
       la vez anterior: es la misma razón por la que los acabados se desmarcan
       en cada apertura. */
    modulos = p.modular ? p.modulos : 0;
    $("#acabadoCant").hidden = !p.modular;
    pintarDatos();

    /* Cómo se compone el precio, solo donde hace falta: hoy únicamente la
       pieza modular, que tiene dos. Va acá y no en la tarjeta porque la grilla
       iguala alturas —una línea de más en una sola tarjeta desalinearía su
       fila entera— y porque este es el momento en que alguien decide cuánto
       pedir. El elemento se oculta del todo (`hidden`) en el resto de las
       piezas: vacío pero presente dejaría un hueco y un renglón en blanco para
       el lector de pantalla. */
    const extra = $("#acabadoExtra");
    if (p.precioModulo) {
      extra.textContent = `Cada ${p.modulos} módulos, ${colones(p.precio)}. Sueltos, ${colones(p.precioModulo)} cada uno.`;
      extra.hidden = false;
    } else {
      extra.textContent = "";
      extra.hidden = true;
    }

    /* La nota del pie remata en el enlace al cotizador, que es fijo; lo que
       cambia es el motivo. "Es la medida estándar" no aplica a la modular: ahí
       lo que se elige es una cantidad de módulos, no una medida, y hablarle de
       medidas invitaría a pedir centímetros que esta pieza no acepta. */
    $("#acabadoNota").textContent = p.modular
      ? `Puedes pedir hasta ${p.maxModulos} módulos. Para más, o para otra forma, `
      : "Es la medida estándar. Si la necesitas en otra, ";

    // Sin preselección, y limpiando lo de la pieza anterior.
    radios.forEach((r) => { r.checked = false; });
    err.hidden = true;

    setOpen(true);
  };

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const marcado = radios.find((r) => r.checked);

    if (!marcado) {
      err.hidden = false;
      radios[0].focus();
      return;
    }

    const p = pieza;
    const cant = modulos;

    agregarAlPedido({
      tipo: "pieza",
      id: p.id,
      acabado: marcado.value,
      // Medida estándar: los `def` de la pieza. Acá no se elige medida — para
      // eso está el cotizador, que enlaza la nota de abajo.
      alto: p.medidas.alto.def,
      ancho: p.medidas.ancho.def,
      largo: p.medidas.largo.def,
      // Entró por la tarjeta, en medida de fábrica: se cobra en línea.
      via: VIA_ESTANDAR,
      /* Solo en la modular, y es lo que decide su precio. Va en la línea (y en
         claveLinea) porque 6 módulos y 10 módulos NO son la misma línea con
         más cantidad: son dos configuraciones distintas del mismo mueble. */
      ...(p.modular ? { modulos: cant } : {}),
    });

    const nombre = p.nombre;
    const acabado = marcado.value.toLowerCase();
    const cuantos = p.modular ? ` (${cant} ${cant === 1 ? "módulo" : "módulos"})` : "";
    setOpen(false);
    showToast(`${nombre}${cuantos} en ${acabado}, agregada al pedido.`);
  });

  $("#acabadoMenos").addEventListener("click", () => cambiarModulos(-1));
  $("#acabadoMas").addEventListener("click", () => cambiarModulos(1));

  cerrar.addEventListener("click", () => setOpen(false));
  scrim.addEventListener("click", () => setOpen(false));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && pieza) setOpen(false);
  });

  ventanaAcabado = { abrir };
}

/* --- Accesorios ------------------------------------------------------------
   Misma grilla de tres columnas que la colección, pero con el marco vertical
   (18:25, en CSS) en lugar de 6:5 — ver el comentario de ACCESORIOS en
   products.js para por qué el marco alto no es una preferencia.
   No hay ficha ni configurador: son de medida fija.                          */
function renderAccesorios() {
  const grid = $("#accGrid");
  if (!grid || typeof ACCESORIOS === "undefined") return;

  grid.innerHTML = ACCESORIOS.map((a, i) => `
    <figure class="acc reveal" data-id="${esc(a.id)}" style="--d:${i * 80}ms"${a.pos ? ` data-pos="${esc(a.pos)}"` : ""}>
      <div class="acc-media"${a.pos ? ` style="--pos:${esc(a.pos)}"` : ""}>
        <img src="${esc(a.image)}" alt="${esc(a.nombre)}"
             width="${a.w}" height="${a.h}" decoding="async" loading="lazy">
      </div>
      <!-- Nombre a la izquierda, precio a la derecha: el mismo renglón que la
           tarjeta de colección, para que las dos grillas se lean igual. La
           descripción queda debajo, como estaba. La aclaración del precio solo
           aparece donde hace falta (hoy la repisa, que se vende por unidad y
           en la foto se ven tres).
           OJO: este comentario vive dentro de un template literal — nada de
           comillas invertidas acá adentro, cierran la plantilla y parten el
           archivo. -->
      <figcaption class="acc-cap">
        <span class="acc-cap-fila">
          <span class="acc-nombre">${a.handle
            ? `<a href="/products/${esc(a.handle)}">${esc(a.nombre)}</a>`
            : esc(a.nombre)}</span>
          ${a.precio ? `<span class="acc-precio">${colones(a.precio)}${
            a.precioNota ? ` <span class="acc-precio-nota">${esc(a.precioNota)}</span>` : ""
          }</span>` : ""}
        </span>
        <span class="acc-texto">${esc(a.texto)}</span>
      </figcaption>

      <!-- Botón de texto y no píldora: son tres en fila y tres píldoras
           oscuras convertirían la grilla en una botonera. Los accesorios no
           se configuran (no tienen acabado ni medidas definidas), así que no
           hace falta ficha: entran al pedido de un click. -->
      <button type="button" class="acc-add" data-id="${esc(a.id)}">Agregar al pedido</button>
    </figure>`).join("");
}

function setupAccesorios() {
  const grid = $("#accGrid");
  if (!grid) return;

  /* Un solo oyente en la grilla, que se repinta entera. La tarjeta ENTERA
     agrega, no solo el botón — misma decisión que en la colección y por la
     misma razón; ver el comentario de renderPiezas(). Acá no hay ventana de
     por medio: los accesorios no se configuran (no tienen acabado ni medidas
     definidos), así que el toque agrega directo, como hacía el botón.
     El botón sigue siendo el único elemento enfocable de la tarjeta. */
  grid.addEventListener("click", (e) => {
    if (String(window.getSelection())) return;

    const card = e.target.closest(".acc");
    if (!card) return;
    const a = ACCESORIOS.find((x) => x.id === card.dataset.id);
    if (!a) return;

    /* El botón agrega; el resto de la tarjeta abre la ficha. Sin handle
       (prototipo suelto, sin Shopify detrás) se comporta como antes. */
    if (!e.target.closest(".acc-add") && a.handle) {
      window.location.href = "/products/" + encodeURIComponent(a.handle);
      return;
    }

    // Medida fija, no se configura: se cobra en línea como lo estándar.
    agregarAlPedido({ tipo: "accesorio", id: a.id, via: VIA_ESTANDAR });
    showToast(`${a.nombre} agregado al pedido.`);
  });
}

/* --- Pedido ----------------------------------------------------------------
   No es un carrito de compra: no hay precios ni pasarela. Es una lista de
   piezas configuradas que termina en una cotización. El nombre en el código es
   `pedido`, no `carrito`, justamente para no prometer lo que no hace.

   Entra TODO el catálogo, y las líneas no son todas iguales:
     · pieza ajustable  → acabado + las tres medidas que eligió el cliente
     · pieza estándar   → acabado + su medida fija
     · accesorio        → ni acabado ni medidas (no están definidos)

   Vive en localStorage y se comparte entre index.html y cotiza.html, que son
   el mismo origen: se agrega en el cotizador y se ve desde cualquiera de las
   dos.                                                                        */
const PEDIDO_KEY = "irrelevnt_pedido";
let pedido = [];

/* `tipo` decide en qué lista buscar. Hoy los ids de PIEZAS y ACCESORIOS no
   chocan, pero depender de eso sería frágil: alcanza con que alguien llame
   "cubo" a un accesorio. */
const buscarProducto = (l) => {
  const lista = l.tipo === "accesorio" ? ACCESORIOS : PIEZAS;
  return Array.isArray(lista) ? lista.find((p) => p.id === l.id) : undefined;
};

function cargarPedido() {
  try {
    const guardado = JSON.parse(localStorage.getItem(PEDIDO_KEY));
    // Se filtra contra el catálogo: si un producto sale, su línea guardada no
    // puede quedar viva en el pedido de nadie.
    pedido = Array.isArray(guardado) ? guardado.filter((l) => buscarProducto(l)) : [];
  } catch (_) {
    pedido = []; // localStorage lleno, bloqueado o con basura: se arranca limpio
  }
}

function guardarPedido() {
  try {
    localStorage.setItem(PEDIDO_KEY, JSON.stringify(pedido));
  } catch (_) { /* modo privado: el pedido vive solo en esta sesión */ }
}

/* Cada línea es producto + acabado + medidas: la misma mesa en espejo y en
   pulido son dos líneas, y dos veces la misma configuración suman cantidad. */
const claveLinea = (l) =>
  [l.tipo, l.id, l.acabado || "", l.alto || "", l.ancho || "", l.largo || "",
   /* El origen entra en la clave: la misma pieza en la misma medida se cobra
      si entró por la tarjeta y se cotiza si entró por el cotizador. Fundirlas
      en una sola línea mandaría a un solo camino dos cosas con destinos
      distintos. */
   l.via || "",
   /* Los módulos entran en la clave: la misma estantería de 6 y de 10 son dos
      configuraciones distintas con dos precios distintos, no una línea con más
      cantidad. Sin esto, pedir 10 después de 6 sumaría cantidad sobre la de 6
      y se cobraría de menos. */
   l.modulos || ""].join("|");

function agregarAlPedido(linea) {
  const ya = pedido.find((l) => claveLinea(l) === claveLinea(linea));
  if (ya) ya.cant += 1;
  else pedido.push({ ...linea, cant: 1 });
  guardarPedido();
  renderPedido();
}

function cambiarCantidad(clave, delta) {
  const l = pedido.find((x) => claveLinea(x) === clave);
  if (!l) return;
  l.cant += delta;
  if (l.cant < 1) pedido = pedido.filter((x) => x !== l);
  guardarPedido();
  renderPedido();
}

/* La ficha que se ve bajo el nombre, distinta según qué sea la línea. */
function detalleLinea(l, p) {
  if (l.tipo === "accesorio") return ["Medida fija"];
  /* La modular usa medidaLinea() —la misma frase que la tarjeta y la ventana—
     porque lo que se agregó es el conjunto de la foto, no una unidad: escribir
     acá sus medidas guardadas (30 × 30 × 40) al lado de ₡180 000 haría pasar
     el precio del conjunto por el de un módulo.

     Las demás siguen mostrando LO QUE SE PIDIÓ (`l.*`, no los `def` de la
     pieza), que en las ajustables puede no ser la medida estándar. El sufijo
     sale de notaMedida() y no de una cadena escrita acá; las ajustables van
     sin sufijo, venga el número de la tarjeta o del cotizador. */
  if (p.modular) {
    return [`Acabado ${String(l.acabado).toLowerCase()}`, medidaLinea(p, l.modulos)];
  }
  return [
    `Acabado ${String(l.acabado).toLowerCase()}`,
    `${l.alto} × ${l.ancho} × ${l.largo} cm${p.ajustable ? "" : " · " + notaMedida(p)}`,
  ];
}

function renderPedido() {
  const cuenta = $("#pedidoCount");
  const btn = $("#pedidoBtn");
  if (!cuenta || !btn) return;

  const total = pedido.reduce((s, l) => s + l.cant, 0);
  cuenta.textContent = String(total);
  /* El botón dice "Pedido 3" y eso, leído en voz alta, es "pedido tres". El
     aria-label lo dice completo. */
  btn.setAttribute("aria-label",
    total === 0 ? "Tu pedido, vacío" : `Tu pedido, ${total} pieza${total === 1 ? "" : "s"}`);

  const cont = $("#pedidoItems");
  if (!cont) return; // el panel lo arma setupPedido(); si aún no existe, solo la cuenta

  if (!pedido.length) {
    /* Registro de "tú", como el resto del sitio ("El mueble que mereces",
       "Cuéntanos"). No mezclar con voseo. */
    cont.innerHTML = `<p class="pedido-vacio">Todavía no has agregado nada.
      Configura una pieza en <a class="link-line" href="cotiza.html">Cotiza a tu medida</a>
      o agrega un accesorio, y aparece aquí.</p>`;
    return;
  }

  cont.innerHTML = pedido.map((l) => {
    const p = buscarProducto(l);
    const clave = claveLinea(l);
    return `
    <article class="pedido-item">
      <img src="${esc(p.image)}" alt="" width="${p.w}" height="${p.h}" loading="lazy">
      <div class="pedido-item-txt">
        <p class="pedido-item-nombre">${esc(p.nombre)}</p>
        ${detalleLinea(l, p).map((t) => `<p class="pedido-item-spec">${esc(t)}</p>`).join("")}
        <p class="pedido-item-precio">${importeLinea(l)}</p>
      </div>
      <div class="pedido-item-cant" role="group" aria-label="Cantidad de ${esc(p.nombre)}">
        <button type="button" data-clave="${esc(clave)}" data-delta="-1"
                aria-label="Quitar una unidad">−</button>
        <span>${l.cant}</span>
        <button type="button" data-clave="${esc(clave)}" data-delta="1"
                aria-label="Agregar una unidad">+</button>
      </div>
    </article>`;
  }).join("");

  pintarTotal();
}

/* El total suma SOLO las líneas con precio, y lo dice. Hoy conviven en el
   mismo panel piezas con importe y piezas que van por cotización; un total a
   secas se leería como "esto es lo que cuesta todo", que sería falso.

   Si no hay ninguna línea con precio el bloque no aparece: el panel vuelve a
   ser exactamente la lista de cotización que era.                            */
function pintarTotal() {
  const foot = $(".pedido-foot");
  if (!foot) return;

  const viejo = $("#pedidoTotal");
  if (viejo) viejo.remove();

  const { cobrables, aCotizar } = repartirPedido();

  /* Los dos botones se muestran u ocultan según lo que haya en el carrito: sin
     nada cobrable no tiene sentido ofrecer pagar, y sin nada a cotizar no hace
     falta el botón de WhatsApp. Con las dos cosas se ven los dos, que es el
     caso mixto que el usuario eligió: se paga lo estándar y se coordina el
     resto aparte, sin obligar a nadie a esperar. */
  $("#pedidoPagar").hidden = !cobrables.length;
  $("#pedidoEnviar").hidden = !aCotizar.length;

  /* Con los dos botones a la vista, pagar es la acción principal y cotizar la
     alternativa: dos píldoras negras idénticas competirían y el ojo no sabría
     cuál es la salida. Si el de cotización queda solo, vuelve a ser el
     principal — no tiene con quién competir. */
  $("#pedidoEnviar").classList.toggle("pill-ghost", cobrables.length > 0);

  const plural = (n, uno, varios) => (n === 1 ? uno : `${n} ${varios}`);
  const bloques = [];

  if (cobrables.length) {
    bloques.push(`
      <p class="pedido-total-fila">
        <span>${plural(cobrables.length, "1 pieza", "piezas")} para pagar</span>
        <strong>${colones(sumaDe(cobrables))}</strong>
      </p>`);
  }

  /* Las de cotización se cuentan pero NO se suman al total, aunque tengan
     precio de lista: lo que se paga es lo otro, y meterlas en la misma cifra
     diría que ese importe es lo que hay que transferir. */
  if (aCotizar.length) {
    bloques.push(`
      <p class="pedido-total-resto">
        ${plural(aCotizar.length, "1 pieza va", "piezas van")} por cotización,
        con precio a confirmar.
      </p>`);
  }

  if (!bloques.length) return;
  foot.insertAdjacentHTML("afterbegin", `
    <div class="pedido-total" id="pedidoTotal">${bloques.join("")}</div>`);
}

/* --- Panel del pedido ------------------------------------------------------
   El panel lo arma JS y no está en el marcado. Es a propósito: el sitio son
   DOS páginas sueltas (no hay build ni plantillas), y tener el mismo bloque
   copiado en index.html y en cotiza.html es la duplicación más fácil de que se
   desincronice. Sin JS no hay pedido posible, así que nada se pierde.
   El header y el pie sí van copiados en las dos: tienen que leerse sin JS.    */
function setupPedido() {
  const btn = $("#pedidoBtn");
  if (!btn || $("#pedido")) return;

  /* El velo va ANTES del panel para que quede por debajo sin inventarle un
     z-index. Los dos con `hidden`: cerrado, el panel no debe ser alcanzable
     con Tab ni anunciado por un lector de pantalla. */
  document.body.insertAdjacentHTML("beforeend", `
    <div class="pedido-scrim" id="pedidoScrim" hidden></div>
    <aside class="pedido" id="pedido" hidden role="dialog" aria-modal="true"
           aria-labelledby="pedidoTitulo">
      <div class="pedido-head">
        <h2 class="micro" id="pedidoTitulo">Tu pedido</h2>
        <button type="button" class="pedido-close" id="pedidoClose" aria-label="Cerrar">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
               fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M6 6 L18 18 M18 6 L6 18" />
          </svg>
        </button>
      </div>
      <div class="pedido-items" id="pedidoItems"></div>
      <div class="pedido-foot">
        <!-- Dos caminos, no uno: lo de medida estándar se paga en línea y lo
             pedido a medida se coordina por WhatsApp. pintarTotal() muestra u
             oculta cada botón según lo que haya en el carrito.

             OJO con lo que dicen estas notas: mientras Tilopay no esté
             conectado, el botón de pago avisa en vez de cobrar, y eso tiene
             que estar escrito. No quitar la advertencia antes de que el cobro
             funcione de verdad. -->
        <p class="pedido-nota">
          Las piezas en medida estándar se pagan aquí mismo. Las que pediste a
          tu medida las confirmamos por WhatsApp antes de cobrarlas.
        </p>
        <button type="button" class="pill pill-block" id="pedidoPagar" hidden>Pagar</button>
        <button type="button" class="pill pill-block" id="pedidoEnviar" hidden>Pedir cotización</button>
      </div>
    </aside>`);

  const panel = $("#pedido");
  const scrim = $("#pedidoScrim");
  const cerrar = $("#pedidoClose");

  let abierto = false;
  let ultimoFoco = null;

  const setOpen = (open) => {
    abierto = open;
    panel.hidden = !open;
    scrim.hidden = !open;
    btn.setAttribute("aria-expanded", String(open));
    document.body.classList.toggle("sin-scroll", open);

    if (open) {
      ultimoFoco = document.activeElement;
      /* El estado vive en `abierto`, no en la clase: la clase se aplica tras
         forzar un reflow para que la transición arranque desde el estado
         cerrado. Si dependiera de un rAF y el frame no llegara, el panel
         quedaría abierto sin poder cerrarse. */
      void panel.offsetWidth;
      panel.classList.add("is-open");
      scrim.classList.add("is-open");
      cerrar.focus();
    } else {
      panel.classList.remove("is-open");
      scrim.classList.remove("is-open");
      /* Al cerrar, el foco vuelve de donde vino. Si ya no existe o era el
         propio <body>, va al botón del pedido: nunca dejarlo suelto. */
      const destino = ultimoFoco && ultimoFoco.isConnected && ultimoFoco !== document.body
        ? ultimoFoco : btn;
      destino.focus();
    }
  };

  btn.addEventListener("click", () => setOpen(!abierto));
  cerrar.addEventListener("click", () => setOpen(false));
  scrim.addEventListener("click", () => setOpen(false));

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && abierto) setOpen(false);
  });

  // Cantidades: un solo oyente en el contenedor, que se repinta entero.
  $("#pedidoItems").addEventListener("click", (e) => {
    const b = e.target.closest("button[data-clave]");
    if (!b) return;
    cambiarCantidad(b.dataset.clave, Number(b.dataset.delta));
  });

  /* El envío del pedido. Sin número se comporta como siempre (avisa); con
     número abre WhatsApp con el pedido ya escrito.

     `window.open` y no un `<a>`: el texto se arma en el momento del clic, con
     lo que haya en el pedido en ese instante. Un `href` habría que mantenerlo
     sincronizado en cada alta, baja y cambio de cantidad, y bastaría con que
     una sola de esas rutas se olvidara de refrescarlo para mandar un pedido
     viejo — que es un error que nadie notaría hasta que llegue el mensaje
     equivocado.

     NO se vacía el pedido al enviar: nada confirma que el mensaje se haya
     mandado de verdad (se abre WhatsApp y ahí la persona decide). Borrarlo
     antes de esa confirmación es la forma más fácil de que alguien pierda un
     carrito armado con cuidado. */
  $("#pedidoEnviar").addEventListener("click", () => {
    const { aCotizar } = repartirPedido();
    if (!aCotizar.length) return showToast("No hay piezas por cotizar.");
    if (!hayWhatsApp()) return showToast("El envío del pedido todavía no está conectado.");
    /* Solo las de cotización. Mandar también las que se pagan en línea haría
       que el mensaje pidiera confirmar cosas que el cliente ya pagó. */
    window.open(enlaceWhatsApp(textoPedido(aCotizar)), "_blank", "noopener");
  });

  $("#pedidoPagar").addEventListener("click", () => {
    const { cobrables } = repartirPedido();
    if (!cobrables.length) return showToast("No hay piezas para pagar.");
    pagarConTilopay(cobrables);
  });

  renderPedido();
}

/* --- Cotiza a tu medida (cotiza.html) --------------------------------------
   Vive en SU PROPIA PÁGINA, no en la portada: la portada es editorial y un
   formulario en medio le rompía el ritmo. Se llega por el botón "Cotiza a tu
   medida" que hay bajo la grilla de colección.

   Entra TODA la colección, no solo las ajustables. Las que no lo son muestran
   una línea con su medida en vez de los tres campos — pero se pueden pedir
   igual, y el acabado sí se elige. Lo decide `ajustable` en products.js.

   ⚠ Sistema Riel y Módulo se venden POR MÓDULOS y el formulario no tiene
   dónde decir cuántos: la línea muestra la medida de un módulo y ya. Es
   decisión del usuario (la cantidad y la forma se acuerdan al confirmar el
   pedido, no en la web) — no es un campo que falte por olvido. Si algún día
   se quiere elegir la cantidad acá, es una decisión nueva.

   El formulario termina en "Agregar al pedido": la cotización se pide después,
   desde el panel, con todo junto. No hay backend — el aviso de que no se envía
   nada está en el panel y no se quita mientras siga así.                     */
function setupCotizador() {
  const form   = $("#cotizaForm");
  const piezas = $("#cotizaPiezas");
  const foto   = $("#cotizaFoto");
  const nombre = $("#cotizaNombre");
  const acab   = $("#cotizaAcabado");
  const campos = $("#cotizaCampos");
  const err    = $("#cotizaError");
  if (!form || !piezas || !foto || !nombre || !acab || !campos || !err) return;
  if (typeof PIEZAS === "undefined" || typeof ACABADOS === "undefined") return;

  const lista = PIEZAS;
  if (!lista.length) return;

  let actual = lista[0];

  /* Selector de pieza y de acabado: el radio nativo se oculta pero SIGUE
     siendo el control — recibe foco, se mueve con las flechas y se anuncia
     como grupo. Lo que se ve es el <span>. Mismo patrón que los chips.
     `insertAdjacentHTML` y no `innerHTML`: el <legend> ya está en el marcado y
     tiene que quedar como primer hijo del <fieldset>. */
  piezas.insertAdjacentHTML("beforeend", lista.map((p, i) => `
    <label class="cot-pieza">
      <input type="radio" name="cot-pieza" value="${esc(p.id)}" ${i === 0 ? "checked" : ""}>
      <span class="cot-pieza-media">
        <img src="${esc(p.image)}" alt="" width="${p.w}" height="${p.h}"
             decoding="async" loading="lazy">
      </span>
      <span class="cot-pieza-nombre">${esc(p.nombre)}</span>
    </label>`).join(""));

  acab.insertAdjacentHTML("beforeend", ACABADOS.map((a, k) => `
    <label class="chip">
      <input type="radio" name="cot-acabado" value="${esc(a)}" ${k === 0 ? "checked" : ""}>
      <span>${esc(a)}</span>
    </label>`).join(""));

  /* Los campos se repintan al cambiar de pieza porque los límites son propios
     de cada una: una consola no acepta el mismo alto que una mesa lateral.
     Las de medida fija muestran una línea en vez de los tres campos. */
  function pintar(p) {
    actual = p;
    foto.src = p.image;
    foto.alt = p.nombre;
    nombre.textContent = p.nombre;

    campos.innerHTML = p.ajustable
      ? EJES.map((e) => {
          const m = p.medidas[e];
          return `
          <div class="med-campo">
            <label for="cot-${e}">${e[0].toUpperCase() + e.slice(1)}</label>
            <input type="number" id="cot-${e}" data-eje="${e}"
                   value="${m.def}" min="${m.min}" max="${m.max}" step="1"
                   inputmode="numeric" aria-describedby="cot-${e}-lim">
            <span class="med-lim" id="cot-${e}-lim">${m.min}–${m.max} cm</span>
          </div>`;
        }).join("")
      : `<p class="med-fija">${medidaLinea(p)}</p>`;
    campos.classList.toggle("med-campos", p.ajustable);

    err.hidden = true;
  }

  piezas.addEventListener("change", (e) => {
    const p = lista.find((x) => x.id === e.target.value);
    if (p) pintar(p);
  });

  /* `submit` y no un click en el botón: así funciona también con Enter desde
     un campo, que es lo que hace cualquiera al terminar de escribir. El
     `novalidate` del formulario deja que la validación de abajo sea la única,
     con un mensaje propio en vez del globo del navegador. */
  form.addEventListener("submit", (e) => {
    e.preventDefault();

    const p = actual;
    const med = {};
    const fuera = [];

    /* Se valida al enviar y no solo al escribir: el campo `number` deja pegar
       cualquier cosa y quien lo usa puede no salir nunca del input.
       Las de medida fija no tienen campos: se guarda su `def` tal cual. */
    for (const eje of EJES) {
      const m = p.medidas[eje];
      if (!p.ajustable) { med[eje] = m.def; continue; }
      const input = campos.querySelector(`#cot-${eje}`);
      const v = Math.round(Number(input.value));
      if (!Number.isFinite(v) || v < m.min || v > m.max) {
        fuera.push(`${eje} entre ${m.min} y ${m.max} cm`);
        input.setAttribute("aria-invalid", "true");
      } else {
        input.removeAttribute("aria-invalid");
        med[eje] = v;
      }
    }

    if (fuera.length) {
      err.textContent = "Revisa las medidas: " + fuera.join("; ") + ".";
      err.hidden = false;
      campos.querySelector('[aria-invalid="true"]').focus();
      return;
    }

    err.hidden = true;
    agregarAlPedido({
      tipo: "pieza",
      id: p.id,
      acabado: form.querySelector('input[name="cot-acabado"]:checked').value,
      ...med,
      /* Todo lo que sale del cotizador va por cotización, **aunque las medidas
         hayan quedado en las de fábrica** (decisión del usuario). El cotizador
         es el camino de "a tu medida": lo que se pide por acá se conversa y se
         confirma, no se cobra en línea. */
      via: VIA_COTIZADOR,
    });
    showToast(`${p.nombre} agregada al pedido.`);
  });

  pintar(actual);
}

/* --- Proceso -------------------------------------------------------------- */
function renderProceso() {
  const ol = $("#pasos");
  if (!ol || typeof PROCESO === "undefined") return;

  ol.innerHTML = PROCESO.map((p, i) => `
    <li class="paso reveal" style="--d:${i * 70}ms">
      <span class="paso-num micro" aria-hidden="true">${esc(p.n)}</span>
      <h3>${esc(p.titulo)}</h3>
      <p>${esc(p.texto)}</p>
    </li>`).join("");
}

/* --- Panel de video --------------------------------------------------------
   El panel se queda fijo y el bloque siguiente sube por encima, como una
   ventana que se cierra. Acá solo se calcula CUÁNTO del panel está ya tapado;
   el recorte lo hace CSS con `clip-path` (ver .reel en styles.css).

   `tapado` sale de la posición del hermano siguiente, no de `scrollY`: así no
   hay que saber a qué altura de la página empieza el panel ni rehacer cuentas
   cuando algo de arriba cambia de alto.                                       */
/* El video no empalma consigo mismo. Medido fotograma a fotograma sobre el
   archivo: entre dos fotogramas seguidos la diferencia media es 1,37 y nunca
   pasa de 2,78 — no hay ningún corte dentro —, pero entre el último y el
   primero es 8,8. Con `loop`, esa diferencia se veía como un brinco en cada
   vuelta. Tampoco había un tramo interno que empalmara: el mejor par posible
   daba 6,15, todavía cuatro veces la variación normal, y costaba la mitad del
   video. O sea: recortar no lo arreglaba.

   Se disuelve en su lugar. Dos <video> del mismo archivo, uno visible y otro
   esperando: cuando al de delante le queda menos de REEL_FADE, el de atrás
   arranca desde cero y se intercambia cuál está a la vista. El corte sigue
   existiendo, pero cae dentro de una disolución de medio segundo y deja de
   verse.

   REEL_FADE tiene que valer lo mismo que la transición de `.reel-video` en
   styles.css. Si cambia uno, cambia el otro.                                  */
const REEL_FADE = 0.5;

function setupReelLoop(videos) {
  let [frente, detras] = videos;
  let cruzando = false;
  let vivo = false;

  const paso = () => {
    if (!vivo) return;
    const dur = frente.duration;
    // `dur` es NaN hasta que hay metadatos; entonces todavía no hay nada que
    // cruzar y se vuelve a mirar en el fotograma siguiente.
    if (dur && !cruzando && dur - frente.currentTime <= REEL_FADE) {
      cruzando = true;
      detras.currentTime = 0;
      detras.play().catch(() => {});
      detras.classList.add("is-front");
      frente.classList.remove("is-front");

      const saliente = frente;
      [frente, detras] = [detras, frente];

      /* El saliente se para cuando ya está en opacidad 0, no antes: pararlo al
         instante del cambio congelaría su último fotograma a media disolución
         y se vería. Se rebobina para que le toque entrar limpio. */
      setTimeout(() => {
        saliente.pause();
        saliente.currentTime = 0;
        cruzando = false;
      }, REEL_FADE * 1000);
    }
    requestAnimationFrame(paso);
  };

  return {
    arrancar() {
      if (vivo) return;
      vivo = true;
      frente.play().catch(() => { /* el navegador puede negarse; no es grave */ });
      requestAnimationFrame(paso);
    },
    parar() {
      vivo = false;
      videos.forEach((v) => v.pause());
    },
  };
}

function setupReelCover() {
  const reel = $("#reel");
  if (!reel) return;

  const videos = [$("#reelVideoA"), $("#reelVideoB")].filter(Boolean);
  if (videos.length && "IntersectionObserver" in window) {
    /* Dos observadores con distinto alcance: uno empieza a bajar el archivo
       cuando el panel está cerca, el otro reproduce solo cuando se ve. El
       video pesa ~10 MB y está a media página: bajarlo desde el arranque
       sería competir con las fotos por el ancho de banda. Los dos <video>
       piden la MISMA url, así que la segunda copia sale de la caché del
       navegador: no se descarga dos veces. */
    new IntersectionObserver((entries, obs) => {
      if (!entries[0].isIntersecting) return;
      videos.forEach((v) => { v.preload = "auto"; v.load(); });
      obs.disconnect();
    }, { rootMargin: "150% 0px" }).observe(reel);

    const loop = setupReelLoop(videos);
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !prefersReducedMotion()) loop.arrancar();
      else loop.parar();
    }, { threshold: 0.05 }).observe(reel);
  }

  // Sin `sticky` ni recorte con movimiento reducido: lo dice el CSS, y acá se
  // sale antes de escribir un `--cover` que allá nadie usaría.
  if (prefersReducedMotion()) return;

  let ticking = false;
  let tapadoDelTodo = false;

  const update = () => {
    ticking = false;
    const alto = reel.offsetHeight;
    const sig = reel.nextElementSibling;
    if (!alto || !sig) return;

    const px = Math.min(Math.max(alto - sig.getBoundingClientRect().top, 0), alto);
    reel.style.setProperty("--cover", px.toFixed(1) + "px");

    const ahora = px >= alto - 0.5;
    if (ahora !== tapadoDelTodo) {
      tapadoDelTodo = ahora;
      reel.classList.toggle("is-covered", ahora);
    }
  };

  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });
  window.addEventListener("resize", update, { passive: true });

  update();
}

/* --- Aparición al hacer scroll -------------------------------------------- */

/* El observador queda accesible para `observarReveal()`. Lo necesita cualquier
   código que CREE o REEMPLACE un `.reveal` después del arranque: el observador
   vigila nodos, no selectores, así que un elemento nuevo con la clase puesta no
   entra solo — se queda en `opacity: 0` para siempre. Pasó de verdad con el
   botón de WhatsApp; ver setupWhatsApp(). */
let revealIO = null;

function setupScrollReveal() {
  const items = $$(".reveal");
  if (!items.length) return;

  if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
    items.forEach((el) => el.classList.add("is-visible"));
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add("is-visible");
      io.unobserve(e.target);
    });
  }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });

  items.forEach((el) => io.observe(el));
  revealIO = io;
}

/* Da de alta un `.reveal` nacido después de setupScrollReveal().

   Sin observador (por movimiento reducido o porque el navegador no lo trae) no
   hay nada que hacer: en ese camino setupScrollReveal() ya destapó todo, y el
   nodo nuevo hereda `is-visible` al copiarle el `className` al viejo. Lo mismo
   si ya venía destapado — volver a observarlo solo gastaría una entrada. */
function observarReveal(el) {
  if (!el || !revealIO) return;
  if (!el.classList.contains("reveal") || el.classList.contains("is-visible")) return;
  revealIO.observe(el);
}

/* Red de seguridad: casi todo el contenido arranca en opacity:0. Si el
   observador no llegara a dispararse, a los 3 segundos se destapa lo que esté
   en pantalla. Nunca dejar contenido dependiendo solo de que el observador
   funcione. */
function revealFailsafe() {
  setTimeout(() => {
    $$(".reveal:not(.is-visible)").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) el.classList.add("is-visible");
    });
  }, 3000);
}

/* --- Header --------------------------------------------------------------- */
function setupHeaderScroll() {
  const header = $("#siteHeader");
  if (!header) return;

  let ticking = false;
  const update = () => {
    header.classList.toggle("is-stuck", window.scrollY > 12);
    ticking = false;
  };

  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });

  update();
}

/* --- Menú móvil ----------------------------------------------------------- */
function setupMobileNav() {
  const burger = $("#burger");
  const nav = $("#mobileNav");
  if (!burger || !nav) return;

  const setOpen = (open) => {
    burger.setAttribute("aria-expanded", String(open));
    nav.hidden = !open;
  };

  burger.addEventListener("click", () => {
    setOpen(burger.getAttribute("aria-expanded") !== "true");
  });

  nav.addEventListener("click", (e) => {
    if (e.target.closest("a")) setOpen(false);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && burger.getAttribute("aria-expanded") === "true") {
      setOpen(false);
      burger.focus();
    }
  });

  /* Al pasar a escritorio el menú desplegable deja de tener sentido: si queda
     abierto, sus enlaces siguen siendo alcanzables con Tab por debajo del
     header. */
  window.matchMedia("(min-width: 901px)").addEventListener("change", (e) => {
    if (e.matches) setOpen(false);
  });
}

/* --- Avisos --------------------------------------------------------------- */
function showToast(message) {
  const box = $("#toasts");
  if (!box) return;

  const el = document.createElement("p");
  el.className = "toast";
  el.textContent = message;
  box.appendChild(el);

  setTimeout(() => el.remove(), 3600);
}

/* --- WhatsApp --------------------------------------------------------------
   WhatsApp es el "backend" de este sitio, y conviene ser claro sobre qué
   significa eso: **no hay servidor ni base de datos**. El pedido se arma como
   texto acá, en el navegador, y se abre WhatsApp con todo escrito para que la
   persona lo envíe. Nada sale solo. Sigue sin haber pasarela de pago: el
   pedido ahora LLEGA, pero el cobro se coordina por la conversación.

   Todo cuelga de `WHATSAPP` en products.js. Con esa constante vacía el sitio
   se comporta como antes de esto — ver `hayWhatsApp()`.                      */
const hayWhatsApp = () => typeof WHATSAPP === "string" && /^\d{8,15}$/.test(WHATSAPP);

/* wa.me abre la conversación con el texto ya escrito en la caja de envío.
   `encodeURIComponent` no es opcional: el mensaje lleva saltos de línea, "×",
   "₡" y acentos, y sin codificar se corta en el primer carácter raro.        */
function enlaceWhatsApp(texto) {
  const base = `https://wa.me/${WHATSAPP}`;
  return texto ? `${base}?text=${encodeURIComponent(texto)}` : base;
}

/* El pedido, en texto plano. Reusa `detalleLinea()` y `precioLinea()` —los
   mismos que pinta el panel— para que el mensaje no pueda decir algo distinto
   de lo que la persona acaba de ver en pantalla.

   WhatsApp no tiene tablas ni viñetas de verdad: se usan guiones y saltos de
   línea, que es lo que se ve bien en el teléfono de quien recibe.            */
function textoPedido(lineas_ = pedido) {
  if (!lineas_.length) return "";

  const lineas = lineas_.map((l) => {
    const p = buscarProducto(l);
    const cant = l.cant > 1 ? ` × ${l.cant}` : "";
    const ficha = detalleLinea(l, p).join(", ");
    return `- ${p.nombre}${cant}\n  ${ficha}\n  ${importeLinea(l)}`;
  });

  /* Los totales se calculan sobre las líneas que se están mandando, no sobre
     el pedido entero: este mensaje puede llevar solo la parte de cotización, y
     sumar lo que el cliente ya pagó daría una cifra que no le corresponde.

     Solo se pone "Total" si TODO lo que va en el mensaje tiene precio firme.
     Con una sola pieza a cotizar, cualquier total sería un número provisional
     presentado como definitivo — y del otro lado se lee como presupuesto. */
  const cobrables = lineas_.filter(seCobra);
  const cierre = [];
  if (cobrables.length === lineas_.length) {
    cierre.push(`Total: ${colones(sumaDe(cobrables))}`);
  } else if (cobrables.length) {
    cierre.push(`Subtotal de lo estándar: ${colones(sumaDe(cobrables))}`);
    cierre.push("El resto queda a confirmar.");
  }

  const encabezado = cobrables.length === lineas_.length
    ? "Hola, quiero hacer un pedido:"
    : "Hola, quiero cotizar estas piezas:";

  return [encabezado, "", ...lineas, "", ...cierre]
    .filter((t, i, a) => !(t === "" && a[i - 1] === "")) // sin dobles espacios en blanco
    .join("\n")
    .trim(); // sin el salto colgando cuando no hay bloque de cierre
}

/* --- Tilopay: la costura del cobro en línea --------------------------------
   ⚠ TODAVÍA NO ESTÁ CONECTADO. Esta función es el único punto por donde va a
   entrar, y hoy avisa en vez de cobrar — el mismo patrón honesto que usaba el
   botón de WhatsApp antes de tener número: nada que aparente una acción que no
   ocurre.

   Lo que YA está resuelto y no hay que rehacer cuando llegue Tilopay:

     · Qué se cobra y qué no. Cada línea guarda `via`, y `repartirPedido()`
       separa cobrables de las que van a cotización. Esta función recibe **solo
       las cobrables**, ya filtradas.
     · Los importes. `precioLinea()` da el precio unitario real de cada línea
       (incluida la modular, que depende de los módulos) y `sumaDe()` el total.
     · Que no se muestre un precio de lista donde el precio no es firme
       (`importeLinea()`).

   Lo que FALTA, y es decisión de negocio antes que de código:

     1. **Credenciales y cuenta de Tilopay.** API key / llave de comercio. NO
        van en este archivo: cualquiera puede leer el JS de una web. Requieren
        un backend mínimo (una función serverless alcanza) que firme la
        solicitud de pago. Esto es lo que convierte al sitio en algo que ya no
        es 100% estático.
     2. **Moneda.** Hoy todo está en colones y `colones()` es el único sitio
        donde se formatea. Si se cobra también en dólares, se decide ahí.
     3. **Datos del cliente**: nombre, correo, teléfono y dirección de entrega.
        El carrito hoy no los pide — no hay formulario de checkout.
     4. **Envío.** El paso 04 del proceso dice que el retiro o el envío corre
        por cuenta del cliente. Si eso sigue igual, el cobro es solo de las
        piezas; si no, hay que sumar un costo de envío al total.
     5. **Qué pasa después de pagar**: página de vuelta, confirmación, y qué se
        hace con el carrito (recién ahí tiene sentido vaciarlo — hoy no se
        vacía nunca, justamente porque nada confirma que algo se completó).

   Mientras tanto, el pedido completo se puede mandar por WhatsApp y el cobro
   se coordina ahí.                                                            */
/* ---- Parche Shopify (2026-08-15) ------------------------------------------
   Reemplaza el aviso por el checkout de verdad. Recibe SOLO las líneas
   cobrables (las de medida estándar); las del cotizador siguen yendo por
   WhatsApp y no pasan por acá.

   El panel del pedido sigue siendo el de siempre, con su estado en
   localStorage: acá no se migra ese estado, se SINCRONIZA al pagar. Por eso lo
   primero es vaciar el carrito de Shopify — si no, dos intentos de pago
   seguidos dejarían todo duplicado del otro lado.

   El id de variante sale de p.variantes, que emite el snippet
   datos-irrelevnt.liquid desde los productos de Shopify.

   LA PIEZA MODULAR es el único caso con cuenta: en Shopify son dos variantes
   (conjunto de 6 y módulo suelto) y el pedido habla de N módulos. Se reparte
   en floor(N/6) conjuntos + N%6 sueltos, que da exactamente los mismos
   importes que precioModular() del prototipo (5 → 175 000, 6 → 180 000,
   11 → 355 000, 12 → 360 000). Comprobado contra esos cuatro valores.        */
function variantePara(p, acabado, titulo) {
  const buscado = titulo || acabado;
  return (p.variantes || []).find((v) => v.titulo === buscado) ||
         (p.variantes || [])[0];
}

async function pagarConTilopay(lineas) {
  const items = [];

  for (const l of lineas) {
    const p = buscarProducto(l);
    if (!p || !(p.variantes || []).length) {
      showToast("Esa pieza todavía no está disponible para pagar en línea.");
      return;
    }

    const props = { Medida: medidaLinea(p, l.modulos) };
    if (l.acabado) props.Acabado = l.acabado;

    if (p.modular) {
      const n = Number(l.modulos) || p.modulos;
      const conjuntos = Math.floor(n / 6);
      const sueltos = n % 6;

      if (conjuntos) {
        const v = variantePara(p, l.acabado, `${l.acabado} / Conjunto de 6 módulos`);
        items.push({ id: v.id, quantity: conjuntos * (l.cant || 1), properties: props });
      }
      if (sueltos) {
        const v = variantePara(p, l.acabado, `${l.acabado} / Módulo suelto`);
        items.push({ id: v.id, quantity: sueltos * (l.cant || 1), properties: props });
      }
      continue;
    }

    const v = variantePara(p, l.acabado);
    items.push({ id: v.id, quantity: l.cant || 1, properties: props });
  }

  if (!items.length) return showToast("No hay piezas para pagar.");

  try {
    await fetch("/cart/clear.js", { method: "POST" });
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!r.ok) throw new Error(await r.text());
    window.location.href = "/checkout";
  } catch (e) {
    /* Sin redirección no hay cobro: mejor decirlo que dejar a alguien mirando
       un botón que no hizo nada. */
    showToast("No se pudo preparar el pago. Escribinos por WhatsApp.");
  }
}

/* Los tres puntos de contacto "sueltos": el botón del cierre y el enlace del
   pie de cada página. El del pedido se conecta en setupPedido().             */
function setupWhatsApp() {
  const btn = $("#waBtn");

  /* Enlaces del pie ("WhatsApp"). Hasta que haya número siguen apuntando al
     ancla de contacto, que es a donde apuntaban desde siempre. */
  if (hayWhatsApp()) {
    $$('[data-wa-link]').forEach((a) => {
      a.href = enlaceWhatsApp("Hola, me gustaría hacerles una consulta.");
      a.target = "_blank";
      a.rel = "noopener";
    });
  }

  if (!btn) return;

  if (!hayWhatsApp()) {
    // Como antes: avisa en vez de simular que abre una conversación.
    btn.addEventListener("click", () => {
      showToast("El WhatsApp todavía no está conectado.");
    });
    return;
  }

  /* Con número, el botón deja de ser `button` y pasa a ser un enlace de
     verdad. Es la regla que este proyecto ya tenía escrita para este control,
     al revés: el `button` existía PORQUE no había a dónde ir. Siendo `<a>` se
     puede abrir en otra pestaña, copiar el enlace y anunciarlo como enlace.
     Se reemplaza el nodo en vez de dejar los dos en el marcado para que sin JS
     no queden ni un botón muerto ni un enlace a medias. */
  const a = document.createElement("a");
  a.className = btn.className;
  a.id = btn.id;
  a.style.cssText = btn.style.cssText;
  a.href = enlaceWhatsApp("Hola, me gustaría hacerles una consulta.");
  a.target = "_blank";
  a.rel = "noopener";
  a.textContent = btn.textContent.trim();
  btn.replaceWith(a);

  /* ⚠ SIN ESTA LÍNEA EL BOTÓN SE QUEDA INVISIBLE. Corregido el 2026-08-15.

     `.reveal` arranca en `opacity: 0` y lo destapa el IntersectionObserver de
     setupScrollReveal(), que observa NODOS, no la clase. Este `<a>` es un nodo
     nuevo que nadie observaba: el `<button>` original sí estaba dado de alta,
     pero desapareció del documento al reemplazarlo.

     El síntoma era desconcertante —"a veces sale y a veces no"— y la
     explicación es revealFailsafe(): a los 3 segundos destapa lo que esté EN
     PANTALLA. Si a esa altura estabas parado en el cierre (recarga con la
     página ahí, o entrada directa a #contacto), el botón aparecía; si llegabas
     scrolleando después, no aparecía nunca. La nota de abajo se veía siempre
     porque ese `<p>` nunca se reemplaza, y por eso el bloque quedaba con texto
     y sin botón. */
  observarReveal(a);

  /* La nota de abajo SE BORRA cuando el enlace está conectado (2026-08-15).

     Historia corta: decía "Sitio de demostración — el enlace de WhatsApp
     todavía no está conectado", después pasó a "Te respondemos por WhatsApp.
     Desde la web todavía no se cobra", el usuario quitó la parte del cobro y
     enseguida pidió quitar el resto. Con razón: con el enlace andando, un
     botón que dice "Escríbenos por WhatsApp" no necesita una línea abajo
     explicando que se responde por WhatsApp.

     Se borra en vez de vaciarse para no dejar un `<p>` de alto cero
     empujando el espaciado del cierre.

     ⚠ El `<p>` SIGUE en index.html a propósito, no es residuo: es lo único
     que se ve si no corre JS o si `WHATSAPP` queda vacío, y en esos dos casos
     el botón no lleva a ningún lado. Ahí la advertencia es lo correcto — la
     regla de este proyecto es no aparentar una acción que no ocurre. Si algún
     día se borra del marcado, el botón muerto queda sin explicación. */
  const nota = a.nextElementSibling;
  if (nota && nota.classList.contains("form-note")) nota.remove();
}

/* --- Arranque ------------------------------------------------------------- */
function init() {
  /* Las dos páginas (index.html y cotiza.html) cargan este mismo archivo. Cada
     setup/render sale antes si no encuentra su elemento, así que no hace falta
     ramificar por página. */
  setupMarquee();
  renderPiezas();
  renderAccesorios();
  renderProceso();

  cargarPedido();
  setupPedido();
  renderPedido();
  setupAccesorios();
  setupAcabado();
  setupCotizador();

  setupReelCover();
  setupScrollReveal();
  revealFailsafe();
  setupHeaderScroll();
  setupMobileNav();
  setupWhatsApp();

  const year = $("#year");
  if (year) year.textContent = new Date().getFullYear();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
